import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import {
  LayoutDashboard,
  ArrowLeftRight,
  Receipt,
  Send,
  QrCode,
  CreditCard,
  Users,
  LogOut,
  DollarSign,
  FileText,
  Activity,
  Building2,
  ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const ADMIN_ROLES = ['Manager', 'Operator'];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isPlatformAdmin = user?.role === 'SmartPayAdmin';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const customerLinks = [
    { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/app/statement', icon: FileText, label: 'Extrato' },
    { to: '/app/transfers/internal', icon: ArrowLeftRight, label: 'Transferir' },
    { to: '/app/pix/receive', icon: QrCode, label: 'Receber Pix' },
    { to: '/app/pix/send', icon: Send, label: 'Enviar Pix' },
    { to: '/app/boleto/pay', icon: Receipt, label: 'Pagar Boleto' },
    { to: '/app/credit/overdraft', icon: CreditCard, label: 'Cheque Especial' },
    { to: '/app/credit/loans', icon: DollarSign, label: 'Empréstimos' },
    { to: '/app/security', icon: ShieldCheck, label: 'Segurança' },
  ];

  const adminLinks = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/customers', icon: Users, label: 'Clientes' },
    { to: '/admin/transactions', icon: ArrowLeftRight, label: 'Transações' },
    ...(user?.role === 'Manager' ? [{ to: '/admin/pix/receipts', icon: QrCode, label: 'Recebimentos Pix' }] : []),
    { to: '/admin/limits', icon: CreditCard, label: 'Limites' },
    { to: '/admin/loans', icon: DollarSign, label: 'Empréstimos' },
    { to: '/admin/external-events', icon: Activity, label: 'Eventos Externos' },
  ];

  const platformLinks = [
    { to: '/platform/dashboard', icon: LayoutDashboard, label: 'Visão Geral' },
    { to: '/platform/tenants', icon: Building2, label: 'Tenants' },
  ];

  const links = isPlatformAdmin ? platformLinks : isAdmin ? adminLinks : customerLinks;
  const roleLabel = isPlatformAdmin
    ? 'Gestor da Plataforma'
    : user?.role === 'Manager'
      ? 'Gestor do Tenant'
      : user?.role === 'Operator'
        ? 'Operador'
        : 'Cliente';

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="w-64 border-r border-border bg-card">
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center border-b border-border px-6">
            <h1 className="text-xl font-bold tracking-tight">ItechPay</h1>
          </div>

          <nav className="flex-1 space-y-1 p-4">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`
                }
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <link.icon className="h-4 w-4" strokeWidth={1.5} />
                {link.label}
              </NavLink>
            ))}
          </nav>

          <Separator className="mx-4" />

          <div className="space-y-2 p-4">
            <div className="flex items-center justify-between px-2">
              <span className="text-xs text-muted-foreground">{roleLabel}</span>
              <ThemeToggle />
            </div>
            <Button variant="ghost" className="w-full justify-start rounded-full" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="mr-3 h-4 w-4" strokeWidth={1.5} />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
